import React, { useEffect, useState } from 'react';
import {
  Layout, Card, Button, Table, Modal, Form, Input, InputNumber, Select, DatePicker,
  Tag, Space, Typography, message, Row, Col, Tooltip, Popconfirm, Switch, Alert, Progress, Divider
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  CheckCircleOutlined, ReloadOutlined, InfoCircleOutlined, AppstoreOutlined,
  CalendarOutlined, ArrowUpOutlined, ArrowDownOutlined, AimOutlined, TagOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import Sidebar from './Sidebar';
import MainHeader from './MainHeader';
import api from '../api';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const DEFAULT_CATEGORIES = ['Sales', 'Customer', 'Financial', 'Operational', 'Learning & Growth', 'Quality', 'Teamwork', 'General'];

const CATEGORY_COLOR_MAP = {
  Financial: 'green',
  Customer: 'cyan',
  Operational: 'blue',
  Sales: 'purple',
  'Learning & Growth': 'geekblue',
  Quality: 'gold',
  Teamwork: 'magenta',
  General: 'default',
};

const DIRECTION_CONFIG = {
  'Higher is Better': {
    color: 'green',
    icon: <ArrowUpOutlined />,
    label: 'Higher is Better',
  },
  'Lower is Better': {
    color: 'orange',
    icon: <ArrowDownOutlined />,
    label: 'Lower is Better',
  },
  'Exact Target': {
    color: 'purple',
    icon: <AimOutlined />,
    label: 'Exact Target',
  },
};

export default function PmsKraManagement() {
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [kras, setKras] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingKra, setEditingKra] = useState(null);
  const [form] = Form.useForm();

  // Filters
  const [filterCategory, setFilterCategory] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchKras = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/pms/kras');
      if (res.data?.success) {
        setKras(res.data.kras || []);
      } else {
        message.error(res.data?.message || 'Failed to load KRAs');
      }
    } catch (e) {
      console.error('Fetch KRAs error:', e);
      message.error(e.response?.data?.message || 'Failed to load KRAs');
    } finally {
      setLoading(false);
    }
  };

  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);

  const fetchOrgMetaData = async () => {
    try {
      const bfResp = await api.get('/admin/business-functions');
      const bfList = bfResp?.data?.data || [];

      const deptFn = bfList.find((f) => String(f.name || '').toLowerCase() === 'department');
      const deptValues = (deptFn?.values || []).map(v => v.value).filter(Boolean);

      const desigFn = bfList.find((f) => ['designation', 'role', 'title'].includes(String(f.name || '').toLowerCase()));
      const desigValues = (desigFn?.values || []).map(v => v.value).filter(Boolean);

      let staffDepts = [];
      let staffDesigs = [];
      try {
        const staffResp = await api.get('/admin/staff');
        const staffList = staffResp?.data?.staff || staffResp?.data?.data || [];
        staffList.forEach(s => {
          const profile = s.profile || s;
          if (profile.department) staffDepts.push(profile.department);
          if (profile.designation) staffDesigs.push(profile.designation);
          if (profile.role) staffDesigs.push(profile.role);
        });
      } catch (_) {}

      const allDepts = Array.from(new Set([...deptValues, ...staffDepts])).filter(Boolean);
      const allDesigs = Array.from(new Set([...desigValues, ...staffDesigs])).filter(Boolean);

      setDepartments(allDepts);
      setDesignations(allDesigs);
    } catch (e) {
      console.error('Failed to fetch org meta data:', e);
    }
  };

  useEffect(() => {
    fetchKras();
    fetchOrgMetaData();
  }, []);

  useEffect(() => {
    if (kras.length > 0) {
      const existingCats = kras.map(k => k.category).filter(Boolean);
      const combined = Array.from(new Set([...DEFAULT_CATEGORIES, ...existingCats]));
      setCategories(combined);
    }
  }, [kras]);

  const handleAddCategory = (e) => {
    if (e) e.preventDefault();
    const trimmed = newCategoryInput.trim();
    if (trimmed) {
      if (!categories.includes(trimmed)) {
        setCategories(prev => [...prev, trimmed]);
      }
      form.setFieldsValue({ category: trimmed });
      setNewCategoryInput('');
      message.success(`Category "${trimmed}" added!`);
    }
  };

  const handleOpenModal = (kra = null) => {
    setEditingKra(kra);
    if (kra) {
      form.setFieldsValue({
        kraCode: kra.kraCode || '',
        kraName: kra.kraName,
        description: kra.description || '',
        department: kra.department || '',
        designation: kra.designation || '',
        category: kra.category || 'General',
        weightage: kra.weightage !== undefined ? Number(kra.weightage) : 0,
        measurementType: kra.measurementType || 'Percentage',
        performanceDirection: kra.performanceDirection || 'Higher is Better',
        unit: kra.unit || '%',
        status: kra.status || (kra.active ? 'Active' : 'Inactive'),
        effectiveFrom: kra.effectiveFrom ? dayjs(kra.effectiveFrom) : null,
        effectiveTo: kra.effectiveTo ? dayjs(kra.effectiveTo) : null,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        kraCode: '',
        category: 'General',
        weightage: 10,
        measurementType: 'Percentage',
        performanceDirection: 'Higher is Better',
        unit: '%',
        status: 'Active',
        effectiveFrom: null,
        effectiveTo: null,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingKra(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      const payload = {
        kraCode: values.kraCode ? String(values.kraCode).trim().toUpperCase() : null,
        kraName: values.kraName,
        description: values.description || null,
        department: values.department || null,
        designation: values.designation || null,
        category: values.category || 'General',
        weightage: values.weightage !== undefined ? Number(values.weightage) : 0,
        measurementType: values.measurementType || 'Percentage',
        performanceDirection: values.performanceDirection || 'Higher is Better',
        unit: values.unit || '%',
        status: values.status || 'Active',
        effectiveFrom: values.effectiveFrom ? values.effectiveFrom.format('YYYY-MM-DD') : null,
        effectiveTo: values.effectiveTo ? values.effectiveTo.format('YYYY-MM-DD') : null,
        active: values.status === 'Active',
      };

      if (editingKra) {
        const res = await api.put(`/admin/pms/kras/${editingKra.id}`, payload);
        if (res.data?.success) {
          message.success('KRA Template updated successfully');
          handleCloseModal();
          fetchKras();
        } else {
          message.error(res.data?.message || 'Failed to update KRA');
        }
      } else {
        const res = await api.post('/admin/pms/kras', payload);
        if (res.data?.success) {
          message.success('KRA Template created successfully');
          handleCloseModal();
          fetchKras();
        } else {
          message.error(res.data?.message || 'Failed to create KRA');
        }
      }
    } catch (e) {
      console.error('Save KRA error:', e);
      message.error(e.response?.data?.message || 'Failed to save KRA');
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await api.delete(`/admin/pms/kras/${id}`);
      if (res.data?.success) {
        message.success('KRA Template deleted successfully');
        fetchKras();
      } else {
        message.error(res.data?.message || 'Failed to delete KRA');
      }
    } catch (e) {
      console.error('Delete KRA error:', e);
      message.error(e.response?.data?.message || 'Failed to delete KRA');
    }
  };

  // Calculate total active weightage sum
  const activeKras = kras.filter(k => k.status === 'Active' || (k.active && k.status !== 'Inactive'));
  const totalWeightage = activeKras.reduce((sum, k) => sum + Number(k.weightage || 0), 0);

  // Filtered KRAs
  const filteredKras = kras.filter(k => {
    const matchesCategory = !filterCategory || k.category === filterCategory;
    const matchesStatus = !filterStatus || (filterStatus === 'Active' ? (k.status === 'Active' || (k.active && k.status !== 'Inactive')) : (k.status === 'Inactive' || (!k.active && !k.status)));
    const matchesSearch = !searchQuery ||
      k.kraName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      k.kraCode?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      k.department?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      k.designation?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesStatus && matchesSearch;
  });

  const columns = [
    {
      title: 'KRA Code & Name',
      key: 'kraName',
      render: (_, record) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: '700', color: '#1e293b', fontSize: '14px' }}>{record.kraName}</span>
            {record.kraCode && (
              <Tag color="geekblue" style={{ borderRadius: '6px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px' }}>
                {record.kraCode}
              </Tag>
            )}
          </div>
          {record.description && (
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '3px' }}>{record.description}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (cat) => (
        <Tag color={CATEGORY_COLOR_MAP[cat] || 'default'} style={{ borderRadius: '10px', fontWeight: '600' }}>
          {cat || 'General'}
        </Tag>
      ),
    },
    {
      title: 'Dept / Designation',
      key: 'deptDesig',
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div><strong>Dept:</strong> {record.department || 'All Departments'}</div>
          <div style={{ color: '#64748b' }}><strong>Desig:</strong> {record.designation || 'All Designations'}</div>
        </div>
      ),
    },
    {
      title: 'Weightage',
      dataIndex: 'weightage',
      key: 'weightage',
      render: (val) => (
        <Tag color="blue" style={{ fontSize: '13px', fontWeight: '700', borderRadius: '12px', padding: '2px 10px' }}>
          {Number(val || 0)}%
        </Tag>
      ),
    },
    {
      title: 'Measurement & Direction',
      key: 'measurementDirection',
      render: (_, record) => {
        const dir = record.performanceDirection || 'Higher is Better';
        const dirCfg = DIRECTION_CONFIG[dir] || DIRECTION_CONFIG['Higher is Better'];
        return (
          <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
            <div style={{ fontWeight: '600', color: '#1e293b' }}>
              {record.measurementType || 'Percentage'}
              {record.unit ? <span style={{ color: '#64748b', fontWeight: '400', marginLeft: '4px' }}>({record.unit})</span> : ''}
            </div>
            <div style={{ marginTop: '3px' }}>
              <Tag color={dirCfg.color} icon={dirCfg.icon} style={{ borderRadius: '8px', fontSize: '11px', fontWeight: '600' }}>
                {dir}
              </Tag>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Effective Period',
      key: 'effectivePeriod',
      render: (_, record) => {
        if (!record.effectiveFrom && !record.effectiveTo) {
          return <span style={{ color: '#94a3b8', fontSize: '12px' }}>Always Active</span>;
        }
        return (
          <div style={{ fontSize: '11px', color: '#334155' }}>
            <CalendarOutlined style={{ marginRight: '4px', color: '#1677ff' }} />
            {record.effectiveFrom ? dayjs(record.effectiveFrom).format('DD MMM YYYY') : 'Beginning'}
            {' - '}
            {record.effectiveTo ? dayjs(record.effectiveTo).format('DD MMM YYYY') : 'Ongoing'}
          </div>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        const isActive = record.status === 'Active' || (record.active && record.status !== 'Inactive');
        return (
          <Tag color={isActive ? 'success' : 'error'} style={{ borderRadius: '10px', fontWeight: '600', padding: '2px 8px' }}>
            {isActive ? 'Active' : 'Inactive'}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit KRA">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: '#1677ff' }} />}
              onClick={() => handleOpenModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete KRA Template?"
            description="Are you sure you want to delete this KRA template?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete KRA">
              <Button type="text" icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar collapsed={collapsed} />
      <Layout style={{ marginLeft: collapsed ? 80 : 200, height: '100vh', overflow: 'hidden', transition: 'margin-left 0.2s' }}>
        <MainHeader collapsed={collapsed} setCollapsed={setCollapsed} title="Performance Management - KRA Library" />
        <Content style={{ margin: '24px 16px', padding: 24, background: '#f5f5f5', height: 'calc(100vh - 64px - 48px)', overflow: 'auto' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            
            {/* KPI Cards Header */}
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Card bodyStyle={{ padding: '16px' }} style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600' }}>TOTAL KRAs</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', marginTop: '4px' }}>{kras.length}</div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bodyStyle={{ padding: '16px' }} style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600' }}>ACTIVE KRAs</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#52c41a', marginTop: '4px' }}>{activeKras.length}</div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bodyStyle={{ padding: '16px' }} style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600' }}>TOTAL ACTIVE WEIGHTAGE</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: totalWeightage === 100 ? '#52c41a' : '#fa8c16', marginTop: '4px' }}>
                    {totalWeightage}%
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bodyStyle={{ padding: '16px' }} style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600' }}>WEIGHTAGE STATUS</div>
                  <Progress
                    percent={Math.min(totalWeightage, 100)}
                    status={totalWeightage === 100 ? 'success' : 'active'}
                    strokeColor={totalWeightage === 100 ? '#52c41a' : '#1677ff'}
                    style={{ marginTop: '8px' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Main Content Card */}
            <Card
              title={<span style={{ fontWeight: '700', fontSize: '16px', color: '#1e293b' }}>KRA Templates Library</span>}
              extra={
                <Space wrap>
                  <Input.Search
                    placeholder="Search KRA, Code, Dept..."
                    style={{ width: 200 }}
                    onSearch={(val) => setSearchQuery(val)}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    allowClear
                  />
                  <Select
                    placeholder="Filter Category"
                    style={{ width: 150 }}
                    value={filterCategory}
                    onChange={(val) => setFilterCategory(val)}
                    allowClear
                  >
                    {categories.map((cat) => (
                      <Option key={cat} value={cat}>{cat}</Option>
                    ))}
                  </Select>
                  <Select
                    placeholder="Filter Status"
                    style={{ width: 130 }}
                    value={filterStatus}
                    onChange={(val) => setFilterStatus(val)}
                    allowClear
                  >
                    <Option value="Active">Active</Option>
                    <Option value="Inactive">Inactive</Option>
                  </Select>
                  <Button icon={<ReloadOutlined />} onClick={fetchKras}>Refresh</Button>
                  <Button type="primary" icon={<PlusOutlined />} shape="round" onClick={() => handleOpenModal()}>
                    New KRA Template
                  </Button>
                </Space>
              }
              style={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}
            >
              <Table
                columns={columns}
                dataSource={filteredKras}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
              />
            </Card>

          </Space>
        </Content>
      </Layout>

      {/* Create / Edit Modal */}
      <Modal
        title={
          <Space>
            <AppstoreOutlined style={{ color: '#1677ff', fontSize: '18px' }} />
            <span style={{ fontWeight: '700', fontSize: '17px', color: '#1e293b' }}>
              {editingKra ? 'Edit KRA Template' : 'Create New KRA Template'}
            </span>
          </Space>
        }
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={null}
        width={880}
        destroyOnClose
        style={{ top: 30 }}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: '16px' }}>
          
          {/* Section: General Details */}
          <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
            <div style={{ fontWeight: '700', color: '#334155', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
              General Information
            </div>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="kraCode"
                  label="KRA Code"
                  tooltip="Unique identifier for each KRA (e.g. KRA-001, KRA-SALES-01)"
                >
                  <Input placeholder="e.g. KRA-001" style={{ textTransform: 'uppercase' }} />
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item
                  name="kraName"
                  label="KRA Name"
                  rules={[{ required: true, message: 'Enter KRA Name (e.g. Sales Achievement)' }]}
                >
                  <Input placeholder="e.g. Sales Revenue, Customer Satisfaction, Quality Compliance..." />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="category"
                  label="Category"
                  rules={[{ required: true, message: 'Select or create category' }]}
                >
                  <Select
                    showSearch
                    placeholder="Select or add category"
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <Divider style={{ margin: '8px 0' }} />
                        <Space style={{ padding: '0 8px 4px', display: 'flex' }}>
                          <Input
                            placeholder="New category..."
                            value={newCategoryInput}
                            onChange={(e) => setNewCategoryInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddCategory(e);
                              }
                            }}
                          />
                          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleAddCategory}>
                            Add
                          </Button>
                        </Space>
                      </>
                    )}
                  >
                    {categories.map((cat) => (
                      <Option key={cat} value={cat}>
                        {cat}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="department" label="Department">
                  <Select
                    showSearch
                    allowClear
                    placeholder="All Departments"
                    options={departments.map(d => ({ label: d, value: d }))}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="designation" label="Designation">
                  <Input placeholder="e.g. Manager (Leave empty for All)" />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Section: Measurement & Criteria */}
          <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
            <div style={{ fontWeight: '700', color: '#334155', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
              Measurement & Performance Criteria
            </div>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="weightage"
                  label="Weightage (%)"
                  rules={[{ required: true, message: 'Enter Weightage %' }]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    formatter={value => `${value}%`}
                    parser={value => value.replace('%', '')}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="measurementType" label="Measurement Type">
                  <Select>
                    <Option value="Percentage">Percentage (%)</Option>
                    <Option value="Numeric">Numeric (#)</Option>
                    <Option value="Currency">Currency (₹)</Option>
                    <Option value="Score">Rating Score (1-5)</Option>
                    <Option value="Boolean">Yes / No</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="unit" label="Unit of Measurement">
                  <Input placeholder="e.g. %, ₹, Clients, Hours, Calls" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="performanceDirection"
                  label="Performance Direction"
                  rules={[{ required: true, message: 'Select performance direction' }]}
                >
                  <Select size="middle">
                    <Option value="Higher is Better">
                      <Space><ArrowUpOutlined style={{ color: '#52c41a' }} /> <strong>Higher is Better</strong> (More is better)</Space>
                    </Option>
                    <Option value="Lower is Better">
                      <Space><ArrowDownOutlined style={{ color: '#fa8c16' }} /> <strong>Lower is Better</strong> (Less is better)</Space>
                    </Option>
                    <Option value="Exact Target">
                      <Space><AimOutlined style={{ color: '#722ed1' }} /> <strong>Exact Target</strong> (Match target exactly)</Space>
                    </Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Select status' }]}>
                  <Select size="middle">
                    <Option value="Active">
                      <Tag color="success" style={{ fontWeight: '600', padding: '2px 8px' }}>Active</Tag> — Available for appraisal cycles
                    </Option>
                    <Option value="Inactive">
                      <Tag color="error" style={{ fontWeight: '600', padding: '2px 8px' }}>Inactive</Tag> — Temporarily disabled
                    </Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Section: Validity Period & Description */}
          <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
            <div style={{ fontWeight: '700', color: '#334155', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
              Validity & Description
            </div>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="effectiveFrom" label="Effective From">
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD MMM YYYY"
                    placeholder="Effective start date"
                    onChange={() => {
                      if (form.getFieldValue('effectiveTo')) {
                        form.validateFields(['effectiveTo']);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="effectiveTo"
                  label="Effective To"
                  dependencies={['effectiveFrom']}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const start = getFieldValue('effectiveFrom');
                        if (!value || !start) return Promise.resolve();
                        if (value.isBefore(start, 'day')) {
                          return Promise.reject(new Error('Effective To cannot be before Effective From'));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD MMM YYYY"
                    placeholder="Effective end date"
                    disabledDate={(current) => {
                      const start = form.getFieldValue('effectiveFrom');
                      return start ? current && current < start.startOf('day') : false;
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="description" label="Description / Objectives" style={{ marginBottom: 0 }}>
                  <Input.TextArea rows={2} placeholder="Describe the key responsibilities, expectations, and criteria for this KRA..." />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
            <Button size="large" onClick={handleCloseModal}>Cancel</Button>
            <Button type="primary" size="large" htmlType="submit">
              {editingKra ? 'Update KRA Template' : 'Create KRA Template'}
            </Button>
          </div>
        </Form>
      </Modal>
    </Layout>
  );
}
